/**
 * Claims Service
 *
 * Handles all database interactions for the user claims system.
 * Provides strongly typed functions for fetching and toggling claims.
 */

import { supabase } from "@/integrations/supabase/client";
import { UserClaim, ToggleClaimResult, ClaimName } from "@/types/claims";

/**
 * Fetch all claims for the current user
 */
export async function fetchUserClaims(): Promise<UserClaim[]> {
  const { data, error } = await supabase.rpc("get_user_claims" as any);

  if (error) {
    console.error("Error fetching user claims:", error);
    throw new Error("Failed to fetch claims");
  }

  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map((row: any) => ({
    id: row.id,
    claimId: row.claim_id,
    claimName: row.claim_name as ClaimName,
    claimCode: row.claim_code,
    claimDescription: row.claim_description,
    isActive: row.is_active,
    activatedAt: new Date(row.activated_at),
  }));
}

/**
 * Toggle a claim by its code
 * Returns the result with the new state, or null if code is invalid
 */
export async function toggleClaimByCode(code: string): Promise<ToggleClaimResult | null> {
  const { data, error } = await supabase.rpc("toggle_claim_by_code" as any, {
    p_code: code.trim(),
  });

  if (error) {
    console.error("Error toggling claim:", error);
    throw new Error("Failed to toggle claim");
  }

  // Function returns empty result if code is invalid
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return null;
  }

  const row = Array.isArray(data) ? data[0] : data;

  if (!row || !row.claim_id) {
    return null;
  }

  return {
    claimId: row.claim_id,
    claimName: row.claim_name as ClaimName,
    claimDescription: row.claim_description,
    isNowActive: row.is_now_active,
    activatedAt: new Date(row.activated_at),
  };
}

/**
 * Validate if a code exists (without toggling)
 */
export async function validateClaimCode(code: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("claims")
    .select("id")
    .eq("code", code.trim())
    .eq("is_active", true)
    .single();

  if (error) {
    // PGRST116 = no rows returned, which is expected for invalid codes
    if (error.code === "PGRST116") {
      return false;
    }
    console.error("Error validating claim code:", error);
    return false;
  }

  return !!data;
}

/**
 * Mask a claim code for display (show first and last 4 chars)
 */
export function maskClaimCode(code: string): string {
  if (code.length <= 8) {
    return "••••••••";
  }
  const first = code.slice(0, 4);
  const last = code.slice(-4);
  return `${first}••••${last}`;
}

/**
 * Format activation date for display
 */
export function formatActivationDate(date: Date): string {
  return date.toLocaleDateString("cs-CZ", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
